import { useState } from 'react';
import { Plus, Trash2, ArrowLeft, ArrowRight } from 'lucide-react';
import type { Project, Criterion } from '../types';

interface ProjectEvaluationProps {
  projects: Project[];
  onProjectsChange: (projects: Project[]) => void;
  criteria: Criterion[];
  onBack: () => void;
  onNext: () => void;
}

export function ProjectEvaluation({
  projects,
  onProjectsChange,
  criteria,
  onBack,
  onNext,
}: ProjectEvaluationProps) {
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectCost, setNewProjectCost] = useState('');

  const addProject = () => {
    if (!newProjectName.trim() || !newProjectCost.trim()) return;

    const newProject: Project = {
      id: `project-${Date.now()}`,
      name: newProjectName.trim(),
      cost: parseFloat(newProjectCost) || 0,
      scores: {},
      finalScore: 0,
      efficiencyScore: 0,
    };

    onProjectsChange([...projects, newProject]);
    setNewProjectName('');
    setNewProjectCost('');
  };

  const removeProject = (id: string) => {
    onProjectsChange(projects.filter(p => p.id !== id));
  };

  const updateProjectScore = (projectId: string, criterionId: string, score: number) => {
    onProjectsChange(
      projects.map(p => {
        if (p.id === projectId) {
          const newScores = { ...p.scores, [criterionId]: score };
          return { ...p, scores: newScores };
        }
        return p;
      })
    );
  };

  const topLevelCriteria = criteria.filter(c => c.parentId === null);
  const allProjectsScored = projects.every(p =>
    topLevelCriteria.every(c => p.scores[c.id] !== undefined)
  );

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-2">Evaluate Projects</h2>
        <p className="text-[var(--text-sec)]">
          Add projects and score them against each criterion. Use a scale of 0-1 (where 1 = best performance).
        </p>
      </div>

      {/* Add Project */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                addProject();
              }
            }}
            placeholder="Project name"
            className="flex-1 px-4 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-consulting-slate-blue-500"
          />
          <input
            type="number"
            value={newProjectCost}
            onChange={(e) => setNewProjectCost(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                addProject();
              }
            }}
            placeholder="Cost/Effort"
            className="w-32 px-4 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-consulting-slate-blue-500"
          />
          <button
            onClick={addProject}
            className="px-4 py-2 bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700 flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add
          </button>
        </div>
      </div>

      {/* Projects Table */}
      {projects.length === 0 ? (
        <div className="text-center py-12 text-[var(--text-ter)] border-2 border-dashed border-[var(--rule)]">
          <p>No projects added yet. Add at least one project to continue.</p>
        </div>
      ) : (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[var(--surface)]">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-[var(--text-pri)]">
                    Project
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-[var(--text-pri)]">
                    Cost
                  </th>
                  {topLevelCriteria.map((criterion) => (
                    <th
                      key={criterion.id}
                      className="px-4 py-3 text-center text-sm font-semibold text-[var(--text-pri)]"
                    >
                      {criterion.name}
                      <div className="text-xs font-normal text-[var(--text-ter)] mt-1">
                        (Weight: {(criterion.globalWeight * 100).toFixed(1)}%)
                      </div>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right text-sm font-semibold text-[var(--text-pri)]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--rule)]">
                {projects.map((project) => (
                  <tr key={project.id} className="hover:bg-[var(--surface)]">
                    <td className="px-4 py-3 font-medium text-[var(--text-pri)]">
                      {project.name}
                    </td>
                    <td className="px-4 py-3 text-[var(--text-sec)]">{project.cost}</td>
                    {topLevelCriteria.map((criterion) => (
                      <td key={criterion.id} className="px-4 py-3">
                        <input
                          type="number"
                          min="0"
                          max="1"
                          step="0.1"
                          value={project.scores[criterion.id] ?? ''}
                          onChange={(e) =>
                            updateProjectScore(
                              project.id,
                              criterion.id,
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className="w-20 px-2 py-1 border border-[var(--rule)] rounded text-center focus:ring-2 focus:ring-consulting-slate-blue-500"
                          placeholder="0-1"
                        />
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => removeProject(project.id)}
                        className="p-2 text-[var(--coral)] hover:bg-[var(--coral-dim)]"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t border-[var(--rule)]">
        <button
          onClick={onBack}
          className="px-4 py-2 text-[var(--text-sec)] hover:bg-[var(--hover-tint)] flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <button
          onClick={onNext}
          disabled={projects.length === 0 || !allProjectsScored}
          className={`px-6 py-3  font-semibold flex items-center gap-2 ${
            projects.length > 0 && allProjectsScored
              ? 'bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700'
              : 'bg-[var(--rule-bright)] text-[var(--text-ter)] cursor-not-allowed'
          }`}
        >
          View Results
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

